import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  VStack,
  Text,
  Heading,
  Flex,
  Spinner,
  Center,
  useDisclosure,
  ButtonGroup,
  Tooltip,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiDollarSign,
  FiRefreshCw,
  FiEye,
  FiAlertCircle,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import { manhourRateAPI } from "../../services/api";
import ManhourRateFormModal from "./ManhourRateFormModal";
import ManhourRateDetailsModal from "./ManhourRateDetailsModal";

const ManhourRateManagement = () => {
  // State management
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true); // ✅ NEW: Track API availability
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Filters - ✅ FIX: Ensure filters don't send empty strings
  const [filters, setFilters] = useState({
    search: "",
    rateType: "",
    isActive: "",
  });

  // Selected rate for editing/viewing
  const [selectedRate, setSelectedRate] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Modals
  const {
    isOpen: isFormModalOpen,
    onOpen: onFormModalOpen,
    onClose: onFormModalClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();

  // ✅ FIX: Clean filter parameters to avoid sending empty strings
  const cleanFilters = (filters) => {
    const cleaned = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].trim() !== '') {
        cleaned[key] = filters[key].trim();
      }
    });
    return cleaned;
  };

  // Fetch rates
  const fetchRates = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        size: pagination.itemsPerPage,
        ...cleanFilters(filters), // ✅ FIX: Use cleaned filters
      };

      console.log("🔍 Fetching rates with params:", params);
      const response = await manhourRateAPI.getManhourRates(params);
      
      if (response.data?.success) {
        setRates(response.data.items || []);
        setPagination(response.data.pagination || pagination);
        setApiAvailable(true);
      } else {
        setError("Failed to fetch manhour rates");
        setApiAvailable(false);
      }
    } catch (error) {
      console.error("Fetch rates error:", error);
      
      // ✅ FIX: Handle different types of API errors
      if (error.response?.status === 404 || error.response?.status === 400) {
        setApiAvailable(false);
        setError("Manhour rates API is not available yet. Please ensure the backend is configured.");
        setRates([]); // Show empty state instead of error
      } else {
        setError(error.response?.data?.message || "Failed to fetch manhour rates");
        setApiAvailable(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch - ✅ FIX: Don't fetch immediately, wait for component mount
  useEffect(() => {
    // Add a small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      fetchRates();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // ✅ FIX: Remove filters dependency to avoid multiple calls

  // ✅ FIX: Separate effect for filter changes
  useEffect(() => {
    if (rates.length > 0 || apiAvailable) { // Only refetch if we've successfully loaded before
      const timer = setTimeout(() => {
        fetchRates();
      }, 300); // Debounce filter changes

      return () => clearTimeout(timer);
    }
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle create new rate
  const handleCreateRate = () => {
    if (!apiAvailable) {
      toast.error("Manhour rates API is not available");
      return;
    }
    setSelectedRate(null);
    onFormModalOpen();
  };

  // Handle edit rate
  const handleEditRate = (rate) => {
    if (!apiAvailable) {
      toast.error("Manhour rates API is not available");
      return;
    }
    setSelectedRate(rate);
    onFormModalOpen();
  };

  // Handle view rate details
  const handleViewRate = (rate) => {
    setSelectedRate(rate);
    onDetailsModalOpen();
  };

  // Handle delete rate
  const handleDeleteRate = async (rateId) => {
    if (!apiAvailable) {
      toast.error("Manhour rates API is not available");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this manhour rate?")) {
      return;
    }

    setActionLoading((prev) => ({ ...prev, [rateId]: true }));
    try {
      await manhourRateAPI.deleteManhourRate(rateId);
      toast.success("Manhour rate deleted successfully");
      fetchRates(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete rate");
    } finally {
      setActionLoading((prev) => ({ ...prev, [rateId]: false }));
    }
  };

  // Handle form submission success
  const handleFormSuccess = () => {
    onFormModalClose();
    fetchRates(pagination.currentPage);
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  // Get rate type badge color
  const getRateTypeBadgeColor = (rateType) => {
    const colors = {
      global: "blue",
      category: "green", 
      activity: "purple",
      skill_level: "orange",
    };
    return colors[rateType] || "gray";
  };

  // Get skill level display name
  const getSkillLevelDisplay = (skillLevel) => {
    const displays = {
      helper: "Helper",
      skilled: "Skilled Worker",
      supervisor: "Supervisor",
      specialist: "Specialist",
      engineer: "Engineer",
    };
    return displays[skillLevel] || skillLevel;
  };

  // ✅ FIX: Show loading state properly
  if (loading && rates.length === 0) {
    return (
      <Box p={6}>
        <VStack align="start" spacing={2} mb={8}>
          <Heading size="xl" color="white">
            Manhour Rate Management
          </Heading>
          <Text color="gray.400" fontSize="lg">
            Manage hourly rates for different categories, activities, and skill levels
          </Text>
        </VStack>
        <Center minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.400">Loading manhour rates...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Page Header */}
      <VStack align="start" spacing={2} mb={8}>
        <Heading size="xl" color="white">
          Manhour Rate Management
        </Heading>
        <Text color="gray.400" fontSize="lg">
          Manage hourly rates for different categories, activities, and skill levels
        </Text>
      </VStack>

      {/* ✅ FIX: Better error handling for API unavailability */}
      {!apiAvailable && (
        <Alert status="warning" mb={6} bg="orange.900" color="orange.100">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">API Not Available</Text>
            <Text fontSize="sm">
              The manhour rates backend API is not yet configured. Please:
            </Text>
            <VStack align="start" pl={4} spacing={1}>
              <Text fontSize="sm">1. Run the database migration: <code>npx sequelize-cli db:migrate</code></Text>
              <Text fontSize="sm">2. Restart your backend server</Text>
              <Text fontSize="sm">3. Ensure the manhourRates routes are registered in app.js</Text>
            </VStack>
          </VStack>
        </Alert>
      )}

      {/* Error Alert */}
      {error && apiAvailable && (
        <Alert status="error" mb={6} bg="red.900" color="red.100">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Box
        bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
        p={6}
        rounded="xl"
        shadow="md"
        border="1px solid"
        borderColor="whiteAlpha.100"
        mb={6}
      >
        <Flex
          justify="space-between"
          align="center"
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <HStack spacing={4} flex={1} width="100%">
            {/* Search Input */}
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search rates..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.200"
                color="white"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 1px #3182ce",
                }}
                isDisabled={!apiAvailable}
              />
            </InputGroup>

            {/* Rate Type Filter */}
            <Select
              placeholder="All Rate Types"
              value={filters.rateType}
              onChange={(e) => handleFilterChange("rateType", e.target.value)}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              color="white"
              maxW="200px"
              isDisabled={!apiAvailable}
            >
              <option value="global">Global</option>
              <option value="category">Category</option>
              <option value="activity">Activity</option>
              <option value="skill_level">Skill Level</option>
            </Select>

            {/* Status Filter */}
            <Select
              placeholder="All Status"
              value={filters.isActive}
              onChange={(e) => handleFilterChange("isActive", e.target.value)}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              color="white"
              maxW="150px"
              isDisabled={!apiAvailable}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>

            {/* Refresh Button */}
            <IconButton
              aria-label="Refresh"
              icon={<FiRefreshCw />}
              onClick={() => fetchRates(pagination.currentPage)}
              variant="outline"
              colorScheme="gray"
              isLoading={loading}
              isDisabled={!apiAvailable}
            />
          </HStack>

          {/* Add New Rate Button */}
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleCreateRate}
            size="md"
            minW="160px"
            isDisabled={!apiAvailable}
          >
            Add New Rate
          </Button>
        </Flex>
      </Box>

      {/* Rates Table */}
      <Box
        bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
        rounded="xl"
        shadow="md"
        border="1px solid"
        borderColor="whiteAlpha.100"
        overflow="hidden"
      >
        {/* ✅ Show setup instructions when API is not available */}
        {!apiAvailable ? (
          <Center py={16}>
            <VStack spacing={6}>
              {/* <Icon as={FiAlertCircle} boxSize={16} color="orange.400" /> */}
              <VStack spacing={2}>
                <Text fontSize="xl" color="white" fontWeight="bold">
                  Manhour Rate Management Setup Required
                </Text>
                <Text color="gray.400" textAlign="center" maxW="md">
                  The manhour rate management system needs to be set up on the backend.
                  Please follow the setup instructions above.
                </Text>
              </VStack>
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={() => {
                  setApiAvailable(true);
                  fetchRates();
                }}
                colorScheme="blue"
                variant="outline"
              >
                Test Connection
              </Button>
            </VStack>
          </Center>
        ) : (
          <>
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Rate Name
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Type
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Hourly Rate
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Overtime
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Status
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Effective Period
                    </Td>
                    <Td fontWeight="bold" color="gray.400" fontSize="sm">
                      Actions
                    </Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {rates.map((rate) => (
                    <Tr key={rate.id} _hover={{ bg: "whiteAlpha.50" }}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" color="white">
                            {rate.name}
                          </Text>
                          {rate.skillLevel && (
                            <Text fontSize="sm" color="gray.400">
                              {getSkillLevelDisplay(rate.skillLevel)}
                            </Text>
                          )}
                          {rate.masterCategory && (
                            <Text fontSize="sm" color="cyan.400">
                              {rate.masterCategory.name}
                            </Text>
                          )}
                          {rate.masterActivity && (
                            <Text fontSize="sm" color="purple.400">
                              {rate.masterActivity.name}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getRateTypeBadgeColor(rate.rateType)}
                          variant="subtle"
                          px={2}
                          py={1}
                          rounded="md"
                          fontSize="xs"
                        >
                          {rate.rateType.replace("_", " ").toUpperCase()}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontWeight="semibold" color="white">
                          {formatCurrency(rate.hourlyRate, rate.currency)}
                        </Text>
                      </Td>
                      <Td>
                        <Text color="white">
                          {rate.overtimeRate 
                            ? formatCurrency(rate.overtimeRate, rate.currency)
                            : "-"}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={rate.isActive ? "green" : "red"}
                          variant="subtle"
                          px={2}
                          py={1}
                          rounded="md"
                          fontSize="xs"
                        >
                          {rate.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" color="white">
                            From: {new Date(rate.effectiveFrom).toLocaleDateString()}
                          </Text>
                          {rate.effectiveTo && (
                            <Text fontSize="sm" color="gray.400">
                              To: {new Date(rate.effectiveTo).toLocaleDateString()}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <ButtonGroup size="sm" variant="outline" spacing={1}>
                          <Tooltip label="View Details">
                            <IconButton
                              aria-label="View rate"
                              icon={<FiEye />}
                              onClick={() => handleViewRate(rate)}
                              colorScheme="blue"
                              size="sm"
                            />
                          </Tooltip>
                          <Tooltip label="Edit Rate">
                            <IconButton
                              aria-label="Edit rate"
                              icon={<FiEdit />}
                              onClick={() => handleEditRate(rate)}
                              colorScheme="green"
                              size="sm"
                            />
                          </Tooltip>
                          <Tooltip label="Delete Rate">
                            <IconButton
                              aria-label="Delete rate"
                              icon={<FiTrash2 />}
                              onClick={() => handleDeleteRate(rate.id)}
                              colorScheme="red"
                              size="sm"
                              isLoading={actionLoading[rate.id]}
                            />
                          </Tooltip>
                        </ButtonGroup>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Empty State */}
            {rates.length === 0 && !loading && (
              <Center py={10}>
                <VStack spacing={3}>
                  <FiDollarSign size={48} color="gray" />
                  <Text fontSize="lg" color="gray.400">
                    {filters.search ? "No rates found" : "No manhour rates available"}
                  </Text>
                  {filters.search && (
                    <Text fontSize="sm" color="gray.500">
                      Try adjusting your search terms
                    </Text>
                  )}
                  {!filters.search && (
                    <Button
                      leftIcon={<FiPlus />}
                      onClick={handleCreateRate}
                      colorScheme="blue"
                      variant="outline"
                      size="sm"
                    >
                      Create First Rate
                    </Button>
                  )}
                </VStack>
              </Center>
            )}
          </>
        )}
      </Box>

      {/* Pagination would go here if needed */}
      
      {/* Modals - Only render if API is available */}
      {apiAvailable && (
        <>
          <ManhourRateFormModal
            isOpen={isFormModalOpen}
            onClose={onFormModalClose}
            rate={selectedRate}
            onSuccess={handleFormSuccess}
          />

          <ManhourRateDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={onDetailsModalClose}
            rate={selectedRate}
          />
        </>
      )}
    </Box>
  );
};

export default ManhourRateManagement;
